import { Component, Input, OnDestroy } from "@angular/core";
import { Template } from '../shared/template.model';
import { NoteService } from "ng/src/app/notes/shared/note.service";
import { HttpErrorResponse } from "@angular/common/http";
import { Log } from '../../logs/log.model';
import { NoteRenderUpdateResult } from "ng/src/app/notes/shared/note.model";
import { Subject } from 'rxjs';
import { takeUntil } from "rxjs/operators";

@Component({
	selector: 'note-refresh',
	template: `
		<ng-container *ngIf="stage === STAGES.WARNING">
			<label>
				This operation will force all notes belonging to this {{template ? 'template' : 'log'}} "{{template ? template.name : log.name}}"
				to update their note property content.
				This will cause the updated and updatedBy properties to be updated as well.
				<br><br>
				If the update matches the contents of the note, the note will not be updated.
				<br><br>
				To continue, click the button below.
			</label>
			<button pButton label="Enable" (click)="stage = STAGES.SELECTION"></button>
		</ng-container>
		
		<ng-container *ngIf="stage === STAGES.SELECTION">
			<label class="p-d-block">
				<span class="p-d-block">All Notes</span>
				<p-inputSwitch [(ngModel)]="allNotes"></p-inputSwitch>
			</label>
			<label *ngIf="!allNotes" class="p-d-block">
				<span class="p-d-block">Notes Created Between</span>
				<p-calendar [(ngModel)]="dateRanges" selectionMode="range"></p-calendar>
			</label>
			<button pButton label="Start" (click)="start()"></button>
		</ng-container>
		
		<ng-container *ngIf="stage === STAGES.LOADING">
			<lb-loading-indicator label="Loading, Please Wait."></lb-loading-indicator>
		</ng-container>
		
		<ng-container *ngIf="stage === STAGES.RESULT && result">
			<h3>Result</h3>
			<div>
				<label class="p-d-block">Found: {{result.found}}</label>
				
				<p-accordion>
					<p-accordionTab header="Updated {{result.attempted.updated.length}}">
						<p-table [value]="result.attempted.updated" styleClass="p-datatable-striped">
							<ng-template pTemplate="header">
								<tr>
									<th>Note</th>
								</tr>
							</ng-template>
							<ng-template pTemplate="body" let-id>
								<tr>
									<td>
										<a href="/#/note-detail?noteId={{id}}&log={{log.id}}" target="_blank">{{id}}</a>
									</td>
								</tr>
							</ng-template>
						</p-table>
					</p-accordionTab>
					
					<p-accordionTab header="Failed {{result.attempted.failed.length}}">
						<p-table [value]="result.attempted.failed" styleClass="p-datatable-striped">
							<ng-template pTemplate="header">
								<tr>
									<th>Note</th>
									<th>Failure Reason</th>
								</tr>
							</ng-template>
							<ng-template pTemplate="body" let-result>
								<tr>
									<td>
		                                <a href="/#/note-detail?noteId={{result[0]}}&log={{log.id}}" target="_blank">{{result[0]}}</a>
									</td>
									<td>
										<label>{{result[1]}}</label>
									</td>
								</tr>
							</ng-template>
						</p-table>
					</p-accordionTab>
				</p-accordion>
			</div>
		</ng-container>
		
		<ng-container *ngIf="stage === STAGES.ERROR">
			<p-message severity="error" [text]="errorMsg"></p-message>
		</ng-container>
	`
})
export class NoteRefreshComponent implements OnDestroy {
	@Input() template?: Template;
	@Input() log!: Log;
	
	allNotes = true;
	result?: Pick<NoteRenderUpdateResult, 'found'> & {
		attempted: Pick<NoteRenderUpdateResult['attempted'], 'updated'> & {
			failed: [string, string][]
		}
	};
	
	errorMsg = 'The operation was unsuccessful.'
	dateRanges?: Date[] = [
		new Date((new Date().getTime() - (24*60*60*1000))),
		new Date()
	];
	
	private readonly onDestroy$ = new Subject();
	readonly STAGES = Stages;
	stage = Stages.WARNING;
	
	constructor(
		private noteService: NoteService
	) {}
	
	ngOnDestroy(): void {
		this.onDestroy$.next();
	}
	
	start() {
		this.stage = Stages.LOADING;
		this.dateRanges?.forEach(d => d.setHours(0, 0, 0, 0));
		
		const type = this.template ? 'template' : 'log';
		const id = this.template?.id ?? this.log.id;
		
		this.noteService.updateNoteRender(type, id, !this.allNotes && this.dateRanges ? { after: this.dateRanges[0], before: this.dateRanges[1] } : undefined)
			.pipe(
				takeUntil(this.onDestroy$)
			)
			.subscribe(result => {
				this.result = {
					found: result.found,
					attempted: {
						updated: result.attempted.updated,
						failed: Object.entries(result.attempted.failed).map(entry => [entry[0], entry[1]])
					}
				};
				this.stage = Stages.RESULT;
			}, error => {
				if (error instanceof HttpErrorResponse) {
					this.errorMsg = `${this.errorMsg} ${error.error?.message}`
				}
				this.stage = Stages.ERROR;
			});
	}
}

enum Stages {
	WARNING,
	SELECTION,
	LOADING,
	RESULT,
	ERROR
}